import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowRight, EnvelopeSimple, MapPin, PaperPlaneTilt, CircleNotch, Sparkle,
} from "@phosphor-icons/react";
import Logo from "@/components/Logo";
import { submitContact } from "@/lib/admin";

const CATEGORIES = [
  "General Inquiry",
  "Feedback",
  "Feature Request",
  "Bug Report",
  "Billing",
  "Other",
];

// Kept identical to About.js — small hook that sets document.title + meta
// description on mount so SEO crawlers pick up per-route metadata.
function usePageMeta({ title, description }) {
  useEffect(() => {
    if (title) document.title = title;
    if (description) {
      let el = document.querySelector('meta[name="description"]');
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", "description");
        document.head.appendChild(el);
      }
      el.setAttribute("content", description);
    }
  }, [title, description]);
}

export default function Contact() {
  usePageMeta({
    title: "Contact MyResumeDesign",
    description:
      "Contact the MyResumeDesign team for support, feedback, feature requests, or general inquiries.",
  });

  const [form, setForm] = useState({
    name: "",
    email: "",
    subject: "",
    category: CATEGORIES[0],
    message: "",
  });
  // Honeypot + timing spam guards. `hp` is a hidden text field bots often
  // auto-fill; `mountTs` is when the form first rendered (ms epoch). The
  // backend rejects submissions where the honeypot is filled or dwell time
  // is under 2.5s.
  const [hp, setHp] = useState("");
  const [mountTs] = useState(() => Date.now());
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!form.name.trim() || !form.email.trim() || !form.subject.trim() || !form.message.trim()) {
      toast.error("Please fill in all required fields");
      return;
    }
    if (!validEmail(form.email.trim())) {
      toast.error("Please enter a valid email address");
      return;
    }
    setSubmitting(true);
    try {
      await submitContact(
        {
          name: form.name.trim(),
          email: form.email.trim(),
          subject: form.subject.trim(),
          category: form.category,
          message: form.message.trim(),
        },
        { honeypot: hp, mountTs }
      );
      setSubmitted(true);
      toast.success("Message received — we'll get back to you soon");
      setForm({ name: "", email: "", subject: "", category: CATEGORIES[0], message: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send message — please try again");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white text-neutral-950 font-sans" data-testid="contact-page">
      {/* Header */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/80 border-b border-black/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-2 text-sm text-neutral-700 hover:text-black" data-testid="contact-back-home">
              <ArrowLeft size={16} weight="bold" />
              <span className="hidden sm:inline">Home</span>
            </Link>
            <div className="h-4 w-px bg-black/20" />
            <Logo />
          </div>
          <Link to="/builder?welcome=1" className="btn-primary text-xs" data-testid="contact-cta-build">
            Start building — Free
            <ArrowRight size={14} weight="bold" />
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="border-b border-black/5">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-14 lg:py-20 animate-fade-up">
          <div className="label-mono mb-6">
            <span className="inline-block w-6 h-px bg-black/40 align-middle mr-2" />
            Get in touch
          </div>
          <h1
            className="font-heading font-black tracking-tighter leading-[0.95] text-4xl sm:text-5xl lg:text-6xl"
            data-testid="contact-hero-heading"
          >
            Contact <span className="shimmer-text">Us.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-base lg:text-lg text-neutral-600 leading-relaxed">
            We value your feedback and are always looking for ways to improve MyResumeDesign. Whether you'd like to report an issue, suggest a new feature, or share your experience, we'd love to hear from you.
          </p>
        </div>
      </section>

      {/* Form + Info */}
      <section className="border-b border-black/5">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-14 lg:py-20">
          <div className="grid grid-cols-12 gap-6 lg:gap-12">
            {/* Contact form */}
            <div className="col-span-12 lg:col-span-8 animate-fade-up" data-testid="contact-form-column">
              <div className="label-mono mb-6">01 / Send us a message</div>
              {submitted && (
                <div
                  className="mb-6 border border-[#2400FF]/30 bg-[#2400FF]/[0.05] p-4 text-sm text-neutral-800"
                  role="status"
                  data-testid="contact-success-banner"
                >
                  <b>Thanks — your message is in.</b> We aim to respond to genuine inquiries within a few business days.
                </div>
              )}
              <form onSubmit={handleSubmit} className="space-y-5" noValidate data-testid="contact-form">
                {/* Honeypot — hidden from real users via CSS + aria-hidden,
                    but many bots blindly fill every input. Any non-empty
                    value tells the backend to silently drop the submission. */}
                <div
                  aria-hidden="true"
                  style={{ position: "absolute", left: "-9999px", top: "-9999px", height: 0, width: 0, overflow: "hidden" }}
                >
                  <label htmlFor="c-website">Website (leave empty)</label>
                  <input
                    id="c-website"
                    type="text"
                    name="website"
                    tabIndex={-1}
                    autoComplete="off"
                    value={hp}
                    onChange={(e) => setHp(e.target.value)}
                    data-testid="contact-honeypot"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <Field label="Full name" htmlFor="c-name" required>
                    <input
                      id="c-name"
                      type="text"
                      required
                      autoComplete="name"
                      value={form.name}
                      onChange={(e) => setField("name", e.target.value)}
                      className="w-full h-11 border border-black/15 px-3 text-sm focus:outline-none focus:border-neutral-950 transition-colors"
                      data-testid="contact-input-name"
                    />
                  </Field>
                  <Field label="Email address" htmlFor="c-email" required>
                    <input
                      id="c-email"
                      type="email"
                      required
                      autoComplete="email"
                      value={form.email}
                      onChange={(e) => setField("email", e.target.value)}
                      className="w-full h-11 border border-black/15 px-3 text-sm focus:outline-none focus:border-neutral-950 transition-colors"
                      data-testid="contact-input-email"
                    />
                  </Field>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <Field label="Subject" htmlFor="c-subject" required>
                    <input
                      id="c-subject"
                      type="text"
                      required
                      value={form.subject}
                      onChange={(e) => setField("subject", e.target.value)}
                      className="w-full h-11 border border-black/15 px-3 text-sm focus:outline-none focus:border-neutral-950 transition-colors"
                      data-testid="contact-input-subject"
                    />
                  </Field>
                  <Field label="Category" htmlFor="c-category" required>
                    <select
                      id="c-category"
                      value={form.category}
                      onChange={(e) => setField("category", e.target.value)}
                      className="w-full h-11 border border-black/15 px-3 text-sm bg-white focus:outline-none focus:border-neutral-950 transition-colors"
                      data-testid="contact-input-category"
                    >
                      {CATEGORIES.map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </Field>
                </div>
                <Field label="Message" htmlFor="c-message" required>
                  <textarea
                    id="c-message"
                    required
                    rows={6}
                    value={form.message}
                    onChange={(e) => setField("message", e.target.value)}
                    className="w-full border border-black/15 px-3 py-2 text-sm leading-relaxed focus:outline-none focus:border-neutral-950 transition-colors resize-y"
                    placeholder="Tell us how we can help…"
                    data-testid="contact-input-message"
                  />
                </Field>
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 pt-2">
                  <button
                    type="submit"
                    disabled={submitting}
                    className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
                    data-testid="contact-submit-button"
                    aria-busy={submitting}
                  >
                    {submitting ? (
                      <>
                        <CircleNotch size={16} weight="bold" className="animate-spin" />
                        Sending…
                      </>
                    ) : (
                      <>
                        <PaperPlaneTilt size={16} weight="bold" />
                        Send Message
                      </>
                    )}
                  </button>
                  <p className="text-xs text-neutral-500">
                    By submitting this form you agree to our{" "}
                    <Link to="/privacy" className="underline hover:text-black">Privacy Policy</Link>.
                  </p>
                </div>
              </form>
            </div>

            {/* Contact info */}
            <aside className="col-span-12 lg:col-span-4 animate-fade-up" style={{ animationDelay: "0.08s" }} data-testid="contact-info-column">
              <div className="label-mono mb-6">02 / Reach us directly</div>
              <div className="border border-black/10 p-6 space-y-6">
                <div>
                  <div className="flex items-center gap-2 text-neutral-950">
                    <MapPin size={18} weight="duotone" className="text-[#2400FF]" />
                    <div className="font-heading font-semibold text-base tracking-tight">Office</div>
                  </div>
                  <address className="mt-3 not-italic text-sm text-neutral-700 leading-relaxed" data-testid="contact-address">
                    <b className="text-neutral-950">MyResumeDesign.com</b>
                    <br />
                    #243, FCI Colony
                    <br />
                    Vanasthalipuram
                    <br />
                    Hyderabad, Telangana — 500070
                    <br />
                    India
                  </address>
                </div>
                <div className="border-t border-black/10 pt-6">
                  <div className="flex items-center gap-2 text-neutral-950">
                    <EnvelopeSimple size={18} weight="duotone" className="text-[#2400FF]" />
                    <div className="font-heading font-semibold text-base tracking-tight">Email</div>
                  </div>
                  <a
                    href="mailto:contact@myresumedesign.com"
                    className="mt-3 block text-sm text-[#2400FF] hover:underline break-all"
                    data-testid="contact-email-link"
                  >
                    contact@myresumedesign.com
                  </a>
                </div>
              </div>
              <p className="mt-6 text-xs text-neutral-500 leading-relaxed" data-testid="contact-info-note">
                We aim to respond to all genuine inquiries as quickly as possible. Your feedback helps us continuously improve the experience for job seekers around the world.
              </p>
            </aside>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-black/10 py-8">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-neutral-500">
          <div className="font-mono flex items-center gap-3">
            <Sparkle size={14} weight="fill" className="text-[#FF2A00]" />
            © 2026 MyResumeDesign
          </div>
          <div className="flex flex-wrap items-center gap-6 font-mono uppercase tracking-widest text-xs">
            <Link to="/about" className="hover:text-black">About</Link>
            <Link to="/contact" className="hover:text-black">Contact</Link>
            <Link to="/help" className="hover:text-black">Help</Link>
            <Link to="/privacy" className="hover:text-black">Privacy</Link>
            <Link to="/terms" className="hover:text-black">Terms</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

function Field({ label, htmlFor, required, children }) {
  return (
    <label htmlFor={htmlFor} className="block">
      <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-500 mb-1.5">
        {label}
        {required && <span className="text-[#FF2A00] ml-1" aria-hidden="true">*</span>}
      </div>
      {children}
    </label>
  );
}
